import { Component, Inject, OnInit, OnDestroy, ElementRef, signal, effect } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { PSIC_COMPONENTS, Field } from '@pacificspecialtyinsurancecompany/psic-ng-components';
import { CallIntentService } from '../services/call-intent.service';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { APP_CONFIG, AppConfig } from 'src/app.config';
import { ConnectService } from '../services/amazon-connect.service';
import { CommonModule } from '@angular/common';
import { globalMessages } from '../shared/global-messages';
import { ToastService } from '../shared/toast/toast.service';
import { MatIconModule } from '@angular/material/icon';
import { Location } from '@angular/common';
import { globalConstants } from '../config/global-constants';
import { LoadingService } from '../shared/loading.service';
import { Store } from '@ngrx/store';
import { selectCallerRole, selectUserAuthenticated } from '../store/selection/selection.selector';
import { combineLatest } from 'rxjs';
import { take } from 'rxjs/operators';
import { selectRecentPolicies } from '../store/recent-policies/recent-policies.selector';

@Component({
    selector: 'app-call-intent',
    standalone: true,
    imports: [CommonModule, MatIconModule, ...PSIC_COMPONENTS, ReactiveFormsModule],
    templateUrl: './call-intent.component.html',
    styleUrl: './call-intent.component.scss',
})
export class CallIntentComponent implements OnInit, OnDestroy {
    form: FormGroup;
    contactId = '';
    isCompact = signal(false);
    private resizeObserver: ResizeObserver;
    showNoSelectionModal = signal(false);

    private categoryMap: Record<string, string[]> = {
        'Policy Acquisition': ['coverage', 'quote', 'applicationSubmittal', 'pointOfSaleTechSupport'],

        'Policy Serving': [
            'servingCoverage',
            'renewal',
            'nonRenewal',
            'conditionalRenewal',
            'notices',
            'endorsement',
            'Submittals Acknowledgement',
        ],

        Billing: ['Payments', 'Bill pay (Guest pay)', 'Payment plan', 'Invoices'],

        'Payment method': ['Paper Invoice', 'CC', 'EFT'],

        Cancellation: ['PCN', 'Cancellation Requests', 'Refund Check', 'Unclaimed Property Checks'],

        Mortgage: ['CVG and/or POI', 'Mortgage and/or Escrow Updates'],

        Moratoriums: ['Binding Suspension', 'CAT Related'],

        'State of Emergency': ['State of Emergency'],

        'Commission Statement': ['Commission Check'],

        Claims: ['Claims Report'],

        'System Related': ['Producer Access Support', 'JIRA'],
    };

    private displayNameMap: Record<string, string> = {
        coverage: 'Coverage',
        quote: 'Quote',
        applicationSubmittal: 'Application Submittal',
        pointOfSaleTechSupport: 'Point of Sale - Tech Support',
        servingCoverage: 'Coverage',
        renewal: 'Renewal',
        nonRenewal: 'Non-Renewal',
        conditionalRenewal: 'Conditional Renewal',
        notices: 'Notices',
        endorsement: 'Endorsement Request',
        JIRA: 'Jira Ticket',
        'Binding Suspension': 'Binding Suspension (NB)',
        'State of Emergency': 'State of Emergency (60 Grace Period)',
        'Claims Report': 'Claims report',
        'Commission Check': 'Commission check',
    };

    coverageField = new Field({
        name: 'coverage',
        type: 'checkbox',
        caption: 'Coverage',
    });

    quoteField = new Field({
        name: 'quote',
        type: 'checkbox',
        caption: 'Quote',
    });

    applicationSubmittalField = new Field({
        name: 'applicationSubmittal',
        type: 'checkbox',
        caption: 'Application Submittal',
    });

    pointOfSaleTechSupportField = new Field({
        name: 'pointOfSaleTechSupport',
        type: 'checkbox',
        caption: 'Point of Sale - Tech Support',
    });

    servingCoverageField = new Field({
        name: 'servingCoverage',
        type: 'checkbox',
        caption: 'Coverage',
    });

    renewalField = new Field({
        name: 'renewal',
        type: 'checkbox',
        caption: 'Renewal',
    });

    nonRenewalField = new Field({
        name: 'nonRenewal',
        type: 'checkbox',
        caption: 'Non-Renewal',
    });

    conditionalRenewalField = new Field({
        name: 'conditionalRenewal',
        type: 'checkbox',
        caption: 'Conditional Renewal',
    });

    noticesField = new Field({
        name: 'Notices',
        type: 'checkbox',
        caption: 'Notices',
    });

    endorsementRequestField = new Field({
        name: 'Endorsement Request',
        type: 'checkbox',
        caption: 'Endorsement Request',
    });

    submittalsAcknowledgementField = new Field({
        name: 'Submittals Acknowledgement',
        type: 'checkbox',
        caption: 'Submittals Acknowledgement',
    });

    paymentsField = new Field({
        name: 'Payments',
        type: 'checkbox',
        caption: 'Payments',
    });

    billPayField = new Field({
        name: 'Bill pay (Guest pay)',
        type: 'checkbox',
        caption: 'Bill pay (Guest pay)',
    });

    paymentPlanField = new Field({
        name: 'Payment plan',
        type: 'checkbox',
        caption: 'Payment plan',
    });

    invoicesField = new Field({
        name: 'Invoices',
        type: 'checkbox',
        caption: 'Invoices',
    });

    paperInvoiceField = new Field({
        name: 'Paper Invoice',
        type: 'checkbox',
        caption: 'Paper Invoice',
    });

    ccField = new Field({
        name: 'CC',
        type: 'checkbox',
        caption: 'CC',
    });

    eftField = new Field({
        name: 'EFT',
        type: 'checkbox',
        caption: 'EFT',
    });

    pcnField = new Field({
        name: 'PCN',
        type: 'checkbox',
        caption: 'PCN',
    });

    cancellationRequestsField = new Field({
        name: 'Cancellation Requests',
        type: 'checkbox',
        caption: 'Cancellation Requests',
    });

    refundCheckField = new Field({
        name: 'Refund Check',
        type: 'checkbox',
        caption: 'Refund Check',
    });

    unclaimedPropertyChecksField = new Field({
        name: 'Unclaimed Property Checks',
        type: 'checkbox',
        caption: 'Unclaimed Property Checks',
    });

    cvgPoiField = new Field({
        name: 'CVG and/or POI',
        type: 'checkbox',
        caption: 'CVG and/or POI',
    });

    mortgageEscrowUpdatesField = new Field({
        name: 'Mortgage and/or Escrow Updates',
        type: 'checkbox',
        caption: 'Mortgage and/or Escrow Updates',
    });

    bindingSuspensionField = new Field({
        name: 'Binding Suspension',
        type: 'checkbox',
        caption: 'Binding Suspension (NB)',
    });

    catRelatedField = new Field({
        name: 'CAT Related',
        type: 'checkbox',
        caption: 'CAT Related',
    });

    producerAccessSupportField = new Field({
        name: 'Producer Access Support',
        type: 'checkbox',
        caption: 'Producer Access Support',
    });

    stateOfEmergencyField = new Field({
        name: 'State of Emergency',
        type: 'checkbox',
        caption: 'State of Emergency (60 Grace Period)',
    });

    commissionCheckField = new Field({
        name: 'Commission Check',
        type: 'checkbox',
        caption: 'Commission Check',
    });

    claimsReportField = new Field({
        name: 'Claims Report',
        type: 'checkbox',
        caption: 'Claims Report',
    });

    jiraTicketField = new Field({
        name: 'JIRA',
        type: 'checkbox',
        caption: 'Jira Ticket',
    });

    constructor(
        private fb: FormBuilder,
        private callIntentService: CallIntentService,
        private amazonConnectService: ConnectService,
        private http: HttpClient,
        private el: ElementRef,
        private toastService: ToastService,
        @Inject(APP_CONFIG) private config: AppConfig,
        private location: Location,
        private loadingService: LoadingService,
        private store: Store
    ) {
        this.form = this.fb.group({
            coverage: [false],
            quote: [false],
            applicationSubmittal: [false],
            pointOfSaleTechSupport: [false],
            servingCoverage: [false],
            renewal: [false],
            nonRenewal: [false],
            conditionalRenewal: [false],
            notices: [false],
            endorsement: [false],
            Payments: [false],
            'Bill pay (Guest pay)': [false],
            'Payment plan': [false],
            Invoices: [false],
            'Paper Invoice': [false],
            CC: [false],
            EFT: [false],
            PCN: [false],
            'Cancellation Requests': [false],
            'Refund Check': [false],
            'Unclaimed Property Checks': [false],
            'CVG and/or POI': [false],
            'Mortgage and/or Escrow Updates': [false],
            'Binding Suspension': [false],
            'CAT Related': [false],
            'Producer Access Support': [false],
            'Claims Report': [false],
            'Commission Check': [false],
            'State of Emergency': [false],
            JIRA: [false],
            'Submittals Acknowledgement': [false],
        });

        effect(() => {
            if (this.callIntentService.isIntentLoading()) return;
            if (!this.callIntentService.lastCustomerIntent.length) return;
            this.loadingService.hideLoading();
            this.applyIntentToForm(this.callIntentService.lastCustomerIntent);
        });
    }

    ngOnInit(): void {
        // TEMP TEST - remove after verification
        // this.applyIntentToForm([
        //     { category: 'Policy Acquisition', intents: ['Coverage', 'Quote'] },
        //     { category: 'Policy Serving', intents: ['Coverage', 'Renewal'] },
        // ]);
        // return;
        // END TEMP TEST

        this.contactId = this.amazonConnectService.contactId || '';
        this.initResizeObserver();

        if (this.callIntentService.isIntentLoading()) {
            this.loadingService.showLoading();
        }

        const raw = history.state?.customerIntent ?? [];
        const customerIntent: Array<{ category: string; intents: string[] }> =
            typeof raw === 'string' ? JSON.parse(raw) : raw;

        if (customerIntent.length) {
            this.applyIntentToForm(customerIntent);
        } else {
            const summary = this.amazonConnectService.callSummary || '';

            if (summary) {
                this.callIntentService.getCallIntent(summary).subscribe({
                    next: (response) => {
                        const body =
                            typeof response?.body === 'string' ? JSON.parse(response.body) : response?.body ?? response;
                        this.applyIntentToForm(body?.customerIntent ?? []);
                    },
                    error: (err) => console.error('Failed to fetch call intent', err),
                });
            }
        }
    }
    private applyIntentToForm(customerIntent: Array<{ category: string; intents: string[] }>): void {
        if (!customerIntent.length) return;

        const patch: Record<string, boolean> = {};

        // build display name (lowercase) -> control[] from displayNameMap
        const displayToControls: Record<string, string[]> = {};
        Object.entries(this.displayNameMap).forEach(([control, display]) => {
            const key = display.toLowerCase();
            if (!displayToControls[key]) displayToControls[key] = [];
            displayToControls[key].push(control);
        });

        // fallback: lowercase intentToControl keys
        const lowerCaseMap: Record<string, string> = {};
        Object.entries(globalConstants.intentToControl).forEach(([display, control]) => {
            lowerCaseMap[display.toLowerCase()] = control;
        });

        customerIntent.forEach((item) => {
            const categoryControls = this.categoryMap[item.category] ?? [];

            item.intents.forEach((intent) => {
                const lowerIntent = intent.toLowerCase();

                // get all controls that map to this display name
                const candidates = displayToControls[lowerIntent] ?? [lowerCaseMap[lowerIntent]];

                // pick the one that belongs to this category, else first candidate
                const control = candidates.find((c) => categoryControls.includes(c)) ?? candidates[0];

                if (control) {
                    patch[control] = true;
                } else {
                    this.toastService.add({
                        type: 'warning',
                        title: '',
                        message: `No mapping found for intent: ${intent}`,
                    });
                }
            });
        });

        this.form.patchValue(patch);
    }

    ngOnDestroy(): void {
        this.resizeObserver?.disconnect();
    }

    private initResizeObserver(): void {
        const target = this.el.nativeElement.querySelector('.call-intent-container');
        if (!target) return;
        this.resizeObserver = new ResizeObserver((entries) => {
            const width = entries[0].contentRect.width;
            if (width === 0) return;
            const compact = width < globalMessages.compactBreakpoint;
            if (compact !== this.isCompact()) {
                this.isCompact.set(compact);
            }
        });
        this.resizeObserver.observe(target);
    }

    private buildCustomerIntent(): Array<{
        category: string;
        intents: string[];
    }> {
        const formValues = this.form.getRawValue();

        const customerIntent: Array<{
            category: string;
            intents: string[];
        }> = [];

        Object.entries(this.categoryMap).forEach(([category, controls]) => {
            const selectedIntents = controls
                .filter((control) => formValues[control])
                .map((control) => this.displayNameMap[control] ?? control);

            if (selectedIntents.length) {
                customerIntent.push({
                    category,
                    intents: selectedIntents,
                });
            }
        });

        return customerIntent;
    }

    confirmSelection(): void {
        const customerIntent = this.buildCustomerIntent();

        if (!customerIntent.length) {
            this.showNoSelectionModal.set(true);
            return;
        }

        const headers = new HttpHeaders({
            'x-api-key': this.config.callSummaryApiKey,
            'Content-Type': 'application/json',
        });

        combineLatest([
            this.store.select(selectCallerRole),
            this.store.select(selectUserAuthenticated),
            this.store.select(selectRecentPolicies),
        ])
            .pipe(take(1))
            .subscribe(([storedRole, storedAuth, recentPolicies]) => {
                const attrs = this.amazonConnectService.attributes || {};

                const Persona = storedRole || attrs.Persona || '';

                const isAuthFromAttrs = attrs.userAuthenticated ?? attrs.UserAuthenticated ?? attrs.isAuthenticated ??
                    attrs.isuserAuthenticated ??
                    attrs.isUserAuthenticated ?? false;
                const UserAuthenticated = storedAuth !== null ? storedAuth : isAuthFromAttrs;

                const PolicyNo = (
                    recentPolicies?.length
                        ? recentPolicies.map((p) => p.policyNumber)
                        : [attrs.policyNumber || attrs.PolicyNumber || ''].filter(Boolean)
                ).join(', ');

                const {
                    policyNumber,
                    Persona: _Persona,
                    userAuthenticated: _userAuthenticated,
                    UserAuthenticated: _UserAuthenticated,
                    ...restAttrs
                } = attrs;

                const payload = {
                    action: 'updateContactAttributes',
                    contactId: this.contactId,
                    data: {
                        ...restAttrs,
                        // escalationSummary: this.amazonConnectService.callSummary || attrs.escalationSummary || '',
                        escalationSummary: this.amazonConnectService.callSummary || '',
                        preEscalationSummary: this.amazonConnectService.preEscalationSummary || '',
                        customerIntent: JSON.stringify(customerIntent),
                        Persona: Persona,
                        userAuthenticated: String(UserAuthenticated),
                        PolicyNo,
                    },
                };

                this.loadingService.showLoading();

                this.http.post<any>(this.config.callAttributesApi, payload, { headers }).subscribe({
                    next: () => {
                        this.callIntentService.lastCustomerIntent = customerIntent;
                        this.toastService.add({
                            type: 'success',
                            title: '',
                            message: 'Call intent successfully recorded',
                        });
                        this.loadingService.hideLoading();
                        this.location.back();
                    },
                    error: () => {
                        this.loadingService.hideLoading();
                        this.location.back();
                    },
                });
            });
    }

    close(): void {
        this.location.back();
    }
}
